import { SuppCoWidget } from './SuppCoWidget';
import { Analytics } from './analytics';
import './styles.css';

// Widget configuration interface
export interface WidgetConfig {
  apiBase: string;
  domain: string;
  version: string;
  debug: boolean;
}

// Default configuration (will be replaced by build process)
const DEFAULT_CONFIG: WidgetConfig = {
  apiBase: WIDGET_CONFIG_API_BASE,
  domain: WIDGET_CONFIG_DOMAIN,
  version: WIDGET_CONFIG_VERSION,
  debug: __DEV__
};

// Validate UPC format (8-14 digits)
function isValidUPC(upc: string): boolean {
  if (!upc) return false;
  return /^\d{8,14}$/.test(upc.trim());
}

// Validate slug format (alphanumeric with hyphens, at least 2 characters)
function isValidSlug(slug: string): boolean {
  if (!slug || slug.length < 2) return false;
  return /^[a-zA-Z0-9-]+$/.test(slug.trim());
}

// Validate product identifier format (either UPC or slug)
function isValidProductIdentifier(identifier: string): boolean {
  if (!identifier) return false;
  return isValidUPC(identifier) || isValidSlug(identifier);
}

// Extract product identifier (UPC or slug) from URL - accepts both SuppCo formats
function extractProductIdentifierFromURL(url: string): string | null {
  if (!url) return null;
  
  try {
    // Support two formats:
    // 1. "https://supp.co/products/upc/[upc]" (8-14 digits)
    // 2. "https://supp.co/products/[slug]" (alphanumeric with hyphens)
    
    // Try UPC format first
    const upcPattern = /^https:\/\/supp\.co\/products\/upc\/(\d{8,14})$/;
    const upcMatch = url.match(upcPattern);
    if (upcMatch && isValidUPC(upcMatch[1])) {
      return upcMatch[1];
    }
    
    // Try slug format
    const slugPattern = /^https:\/\/supp\.co\/products\/([a-zA-Z0-9-]+)$/;
    const slugMatch = url.match(slugPattern);
    if (slugMatch && isValidSlug(slugMatch[1])) {
      return slugMatch[1];
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to extract product identifier from URL:', url, error);
    return null;
  }
}

// Validate that URL follows exact SuppCo format and has proper rel attributes for SEO
function validateSuppCoURL(element: HTMLAnchorElement, productIdentifier: string): boolean {
  const href = element.href;
  const rel = element.getAttribute('rel');
  
  if (!href) {
    console.error(`SuppCo Widget: Missing href attribute. URL is required for SEO.`);
    return false;
  }

  // Check for exact SuppCo URL format - support both UPC and slug formats
  let expectedUrl: string;
  if (isValidUPC(productIdentifier)) {
    expectedUrl = `https://supp.co/products/upc/${productIdentifier}`;
  } else {
    expectedUrl = `https://supp.co/products/${productIdentifier}`;
  }
  

  if (href !== expectedUrl) {
    console.error(`SuppCo Widget: URL must be exactly "${expectedUrl}" for SEO benefits. Got: "${href}"`);
    return false;
  }

  // Check rel attribute - only allow no rel, or "external" and/or "noopener" for maximum SEO juice
  if (rel && rel.trim() !== '') {
    const relValues = rel.trim().toLowerCase().split(/\s+/);
    const allowedRelValues = ['external', 'noopener'];
    
    // Check if all rel values are in the allowed list
    const hasInvalidRel = relValues.some(value => !allowedRelValues.includes(value));
    
    if (hasInvalidRel) {
      console.error(`SuppCo Widget: rel="${rel}" reduces SEO juice. Only "external" and/or "noopener" allowed, or remove rel attribute entirely.`);
      return false;
    }
  }

  return true;
}

// Track if we've already set up global listeners to prevent duplicates
let globalListenersSetup = false;

// Initialize widgets on page
function initWidgets(config: WidgetConfig = DEFAULT_CONFIG): void {
  if (config.debug) {
    console.log('🚀 Initializing SuppCo widgets...');
    console.log('🔧 Config:', config);
  }

  // Find all widget elements that haven't been processed yet
  const productWidgets = document.querySelectorAll('a.product-widget:not([data-supp-initialized]):not([data-supp-skipped])');
  const badgeWidgets = document.querySelectorAll('a.trust-score-badge:not([data-supp-initialized]):not([data-supp-skipped])');
  const trustInfoWidgets = document.querySelectorAll('a.trust-info-widget:not([data-supp-initialized]):not([data-supp-skipped])');
  
  const allWidgets = [...productWidgets, ...badgeWidgets, ...trustInfoWidgets];
  
  if (config.debug) {
    console.log(`🔍 Found ${allWidgets.length} widgets to process`);
    console.log(`  - ${productWidgets.length} product widgets`);
    console.log(`  - ${badgeWidgets.length} trust badge widgets`);
    console.log(`  - ${trustInfoWidgets.length} trust info widgets`);
  }

  if (allWidgets.length === 0) {
    if (config.debug) {
      console.warn('⚠️ No SuppCo widgets found. Make sure you have elements with:');
      console.warn('   - .product-widget, .trust-score-badge, or .trust-info-widget classes');
      console.warn('   - href="https://supp.co/products/upc/[UPC]" (exact format required)');
      console.warn('   - no rel attribute (best for SEO) or only rel="external"/"noopener"');
    }
    return;
  }

  allWidgets.forEach((element, index) => {
    const anchorElement = element as HTMLAnchorElement;
    
    // Extract product identifier (UPC or slug) from URL
    const productIdentifier = extractProductIdentifierFromURL(anchorElement.href);
    
    const widgetType = anchorElement.classList.contains('trust-score-badge') ? 'badge' : 
                      anchorElement.classList.contains('trust-info-widget') ? 'trust-info' : 'product';
    
    if (config.debug) {
      console.log(`🔍 Processing widget ${index + 1} (type: ${widgetType}):`, element);
      console.log(`🔍 Product identifier extracted from SuppCo URL: "${productIdentifier}"`);
      console.log(`🔍 Href: "${anchorElement.href}"`);
    }

    // Create analytics instance for validation tracking
    const analytics = new Analytics(config.version, config.debug);

    // Validate product identifier format
    if (!isValidProductIdentifier(productIdentifier || '')) {
      console.warn(`SuppCo Widget: Could not extract valid product identifier from URL "${anchorElement.href}" - preserving original link for SEO`);
      
      // Track validation failure
      analytics.trackWidgetValidation(productIdentifier || 'invalid', false, 'invalid_product_identifier_format', {
        widget_type: widgetType,
        url: anchorElement.href,
        rel_attribute: anchorElement.getAttribute('rel')
      });
      
      // Don't replace the link - preserve original for SEO juice
      // Mark as processed so we don't try again
      anchorElement.setAttribute('data-supp-skipped', 'invalid-format');
      return;
    }

    // Store extracted product identifier in data attribute for internal use
    if (productIdentifier) {
      anchorElement.dataset.productIdentifier = productIdentifier;
      // Also store as UPC for backward compatibility if it's a UPC
      if (isValidUPC(productIdentifier)) {
        anchorElement.dataset.upc = productIdentifier;
      }
    }

    // Validate strict SuppCo URL format and rel attribute for ALL widget types
    if (!validateSuppCoURL(anchorElement, productIdentifier!)) {
      console.warn(`SuppCo Widget: URL validation failed - preserving original link for SEO`);
      
      // Track validation failure
      analytics.trackWidgetValidation(productIdentifier!, false, 'url_validation_failed', {
        widget_type: widgetType,
        url: anchorElement.href,
        rel_attribute: anchorElement.getAttribute('rel'),
        domain: new URL(anchorElement.href).hostname
      });
      
      // Don't replace the link - preserve original for SEO juice
      // Mark as processed so we don't try again
      anchorElement.setAttribute('data-supp-skipped', 'validation-failed');
      return;
    }

    // Track successful validation
    analytics.trackWidgetValidation(productIdentifier!, true, 'passed', {
      widget_type: widgetType,
      url: anchorElement.href,
      rel_attribute: anchorElement.getAttribute('rel')
    });

    // Mark as initialized before processing
    anchorElement.setAttribute('data-supp-initialized', 'true');
    anchorElement.setAttribute('data-widget-type', widgetType);

    // Store original href for fallback
    if (anchorElement.href) {
      anchorElement.setAttribute('data-original-href', anchorElement.href);
    }

    if (config.debug) {
      console.log(`✅ Creating SuppCoWidget for product identifier: ${productIdentifier} (type: ${widgetType})`);
    }
    try {
      new SuppCoWidget(anchorElement, config);
    } catch (error) {
      console.warn('Failed to initialize SuppCo widget:', error);
      analytics.trackError(productIdentifier!, 'widget_initialization_failed', {
        widget_type: widgetType,
        error_message: error instanceof Error ? error.message : 'unknown_error'
      });
      showWidgetError(anchorElement, productIdentifier!, config);
    }
  });
}

// Setup global listeners only once
function setupGlobalListeners(): void {
  if (globalListenersSetup) {
    return;
  }
  
  globalListenersSetup = true;
  
  // Watch for dynamic additions using MutationObserver
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      let shouldInit = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              const element = node as Element;
              // Check for widget elements (with or without data-upc since we now extract from URLs)
              if (element.tagName === 'A' && 
                  (element.classList?.contains('product-widget') || 
                   element.classList?.contains('trust-score-badge') || 
                   element.classList?.contains('trust-info-widget'))) {
                shouldInit = true;
              } else if (element.querySelector?.('a.product-widget, a.trust-score-badge, a.trust-info-widget')) {
                shouldInit = true;
              }
            }
          });
        }
      });

      if (shouldInit) {
        initWidgets();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Prevent multiple initializations from multiple script inclusions
if (!window.SuppCoWidget) {
  // Auto-initialize widgets when DOM is ready (only if not already done)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initWidgets();
      setupGlobalListeners();
    });
  } else {
    initWidgets();
    setupGlobalListeners();
  }

  window.SuppCoWidget = {
    init: (config?: Partial<WidgetConfig>) => {
      initWidgets({ ...DEFAULT_CONFIG, ...config });
    },
    version: DEFAULT_CONFIG.version,
    enableDebug: () => {
      console.log('🐛 Enabling debug mode on existing widgets...');
      
      // Set up analytics handler if not present
      if (typeof (window as any).suppcoWidgetAnalytics !== 'function') {
        (window as any).suppcoWidgetAnalytics = function(eventData: any) {
          console.log('📊 SuppCo Widget Event:', eventData.event, eventData.properties);
        };
        console.log('📊 Analytics handler created');
      }
      
      // Find all initialized widgets and trigger a sample event to test
      const initializedWidgets = document.querySelectorAll('[data-supp-initialized="true"]');
      console.log(`🔍 Found ${initializedWidgets.length} initialized widgets`);
      
      // Create a debug analytics instance and fire a test event
      const debugAnalytics = new Analytics(DEFAULT_CONFIG.version, true);
      debugAnalytics.trackClick('debug', 'debug_mode_enabled', {
        initialized_widgets_count: initializedWidgets.length,
        timestamp: Date.now()
      });
      
      console.log('✅ Debug mode enabled! Analytics events will now show in console.');
      console.log('💡 Interact with widgets to see events: hover, click, modal open, etc.');
      
      return initializedWidgets.length;
    }
  };
}

// Show validation error while preserving link functionality (currently unused)
function _showValidationError(element: HTMLAnchorElement, message: string): void {
  element.setAttribute('data-supp-initialized', 'true');
  element.style.textDecoration = 'none';
  element.innerHTML = `
    <div class="supp-widget-error">
      <p style="color: #dc2626; font-size: 14px;">${message}</p>
      <p style="font-size: 12px; margin-top: 8px;">
        <a href="${element.href || '#'}" 
           style="color: #3b82f6; text-decoration: underline;">
          ${element.dataset.originalText || element.textContent || 'View Product'}
        </a>
      </p>
    </div>
  `;
}

// Show widget error while preserving link functionality
function showWidgetError(element: HTMLAnchorElement, productIdentifier: string, config: WidgetConfig): void {
  const originalHref = element.getAttribute('data-original-href') || element.href;
  element.style.textDecoration = 'none';
  element.innerHTML = `
    <div class="supp-widget-error">
      <p>Widget failed to load</p>
      <p style="font-size: 12px; margin-top: 8px;">
        <a href="${originalHref}" 
           style="color: #3b82f6; text-decoration: underline;">
          View Product Details
        </a>
      </p>
      <p style="font-size: 11px; margin-top: 4px;">
        <a href="${config.domain}/products/new?barcode=${productIdentifier}" 
           target="_blank" rel="noopener" style="color: #6b7280; text-decoration: none;">
          Can't find this product? Add it to SuppCo →
        </a>
      </p>
    </div>
  `;
}

// Export for global access
declare global {
  const __DEV__: boolean;
  const WIDGET_CONFIG_API_BASE: string;
  const WIDGET_CONFIG_DOMAIN: string;
  const WIDGET_CONFIG_VERSION: string;
  
  interface Window {
    SuppCoWidget: {
      init: (config?: Partial<WidgetConfig>) => void;
      version: string;
      enableDebug: () => number;
    };
  }
}

// Export for manual initialization, debugging, and build process
export { initWidgets, SuppCoWidget, DEFAULT_CONFIG, Analytics }; 